import { html, LitElement, nothing, PropertyValues } from 'lit';
import { styles } from '../../../view/styles';
import { InlineTagFormWebComponent } from '../../../../../tagsManager/ui/inlineTagForm';
import { translateFn } from '../../../../../utils/translator.helper';
import { customElement, property, query } from 'lit/decorators.js';
import { GridType, RuleType, Status } from '../../../../customRulesGrid/types';
import { ConsoleRequest, DataProviderType } from '../../../../../utils/request';
import { RULE_DESCRIPTION_MAX_LENGTH, RULE_NAME_MAX_LENGTH } from '../../../constants';
import { RegexValidator } from '../../../../../ui/gzForm/validators/regex.validator';
import { GzTextareaWebComponent } from '../../../../../ui/gzInput';
import { GzInputWebComponent } from '../../../../../ui/gzInput/component/GzInputWebComponent';
import { GzTooltip } from '../../../../../ui/gzTooltip';

@customElement('gz-rule-configuration')
export class GzRuleConfigurationWebComponent extends LitElement {
  @property({ type: String, attribute: false, reflect: false })
  gridType;

  @property({ type: Array, attribute: false, reflect: false })
  tags;

  @property({ type: Object, attribute: false, reflect: false })
  rule: any;

  @query('#name')
  private readonly nameInput: GzInputWebComponent;

  @query('#description')
  private readonly descriptionInput: GzTextareaWebComponent;

  @query('#status')
  private readonly statusCheckbox;

  @query('#rule-tags')
  private readonly ruleTags;

  private inlineTagForm: InlineTagFormWebComponent;

  @property({ type: Boolean, attribute: false, reflect: false })
  addTagOpen = false;

  static get styles() {
    return styles;
  }

  constructor() {
    super();

    this.initInlineTagForm();
  }

  private ruleTagInputState() {
    if (this.ruleTags.searchInput) {
      this.ruleTags.searchInput.disabled = this.addTagOpen;
    }
  }

  initInlineTagForm() {
    this.inlineTagForm = new InlineTagFormWebComponent();
    this.inlineTagForm.classList.add('mt-1');
    this.inlineTagForm.minLength = 2;
    this.inlineTagForm.maxLength = 128;
    this.inlineTagForm.customValidators = [
      RegexValidator(
        /^[^\s<>\'\"][^<>\'\"]*$/,
        translateFn('The rule tag contains characters that are not allowed.')
      ),
    ];

    this.inlineTagForm.injectDependencies({
      translateFn: translateFn,
    });

    this.inlineTagForm.start().then().catch();
    if (this.inlineTagForm.controller) {
      this.inlineTagForm.controller.view.dom.$tagNameListItem.noBackend = true;
    }

    this.inlineTagForm.addEventListener('customTagAdded', (event: CustomEvent) => {
      const tagName = event.detail.tagName;

      if (this.tags.some(tag => tag.name === tagName)) {
        return;
      }

      this.tags.push({ name: tagName });
      this.ruleTags.filterOptions = this.getTagsOptions();

      this.inlineTagForm.dispatchEvent(
        new CustomEvent('tagButtonInteract', {
          detail: {
            addNewTagClicked: false,
          },
          bubbles: true,
          composed: true,
        })
      );
    });
  }

  protected updated(_changedProperties: PropertyValues) {
    super.updated(_changedProperties);
    this.ruleTagInputState();
  }

  render() {
    const tagsOptions = this.getTagsOptions();
    return html`
      <div class="d-flex flex-column">
        <gz-text class="section-title" fontSize="18" fontweight="500">${translateFn('Rule configuration')}</gz-text>

        <div class="d-flex flex-row mt-3 mb-1 align-items-center">
          <gz-tooltip class="label rule-configuration-label" tooltipText="${translateFn('Rule name')}" isEllipsis>
            <gz-text fontSize="13">${translateFn('Rule name')}</gz-text>
          </gz-tooltip>
          <gz-input
            id="name"
            .placeholder="${translateFn('Type here')}"
            .value="${this.rule.name}"
            @change="${this.handleRuleNameInput}"
            ?disabled="${this.addTagOpen}"
            inputWidth="320"
          ></gz-input>
        </div>

        <div class="d-flex flex-row mb-1 align-items-start">
          <gz-tooltip class="label rule-configuration-label" tooltipText="${translateFn('Description')}" isEllipsis>
            <gz-text fontSize="13">${translateFn('Description')}</gz-text>
          </gz-tooltip>
          <gz-textarea
            .placeholder="${translateFn('Type here')}"
            id="description"
            inputWidth="320"
            hasCounter
            maxchars="${RULE_DESCRIPTION_MAX_LENGTH}"
            @change="${this.handleRuleDescriptionInput}"
            .value="${this.rule.details ? this.rule.details : ''}"
            ?disabled="${this.addTagOpen}"
          ></gz-textarea>
        </div>

        <div class="d-flex flex-row align-items-start mb-3">
          <gz-tooltip class="label rule-configuration-label" tooltipText="${translateFn('Rule tags')}" isEllipsis>
            <gz-text fontSize="13">${translateFn('Rule tags')}</gz-text>
          </gz-tooltip>
          <div class="d-flex flex-column content">
            <gz-enum-list
              id="rule-tags"
              class="rule-tags"
              .searchPlaceholderString="${translateFn('Search tags')}"
              .filterOptions="${tagsOptions}"
              .allItemsLabel="${translateFn('All')}"
              .noSearchResultsString="${translateFn('No results found')}"
              canSelectAll
              @optionChanged="${this.handleTagsChange}"
            ></gz-enum-list>
            ${this.inlineTagForm}
          </div>
        </div>

        <div class="d-flex flex-row align-items-center">
          <gz-checkbox
            id="status"
            .label=${this.gridType === GridType.EXCLUSION
              ? translateFn('Enable exclusion rule')
              : translateFn('On-access scanning')}
            .checked="${this.rule.status === Status.ENABLED}"
            @change="${this.handleStatusCheckboxChange}"
            ?disabled="${this.addTagOpen}"
          ></gz-checkbox>
          ${this.rule.type === RuleType.YARA ? this.renderExperimentalTag() : nothing}
        </div>
        ${this.rule.type === RuleType.YARA ? this.renderYaraInfo() : nothing}
      </div>
    `;
  }

  renderYaraInfo() {
    return html` <div class="d-flex flex-column yara-description">
      <gz-text fontSize="12" fontWeight="200">
        ${translateFn('YARA rules may cause network performance issues, proceed with care.')}
      </gz-text>
      <gz-text fontSize="12" fontWeight="200">
        ${translateFn('YARA rules do not supersede existing policies or custom rule exclusions.')}
      </gz-text>
    </div>`;
  }

  renderExperimentalTag() {
    return html` <gz-chip .text="${translateFn('Experimental')}" class="ml-2" notselectable></gz-chip> `;
  }

  private handleTagsChange() {
    const tags = this.ruleTags.getCheckedOptions();
    this.rule.tags = tags.map(tag => tag.value);
  }

  private handleRuleNameInput(e: CustomEvent) {
    this.rule.name = e.detail.sourceEvent;
    this.nameInput.markAsValid();

    this.validateRuleName();
  }

  private handleRuleDescriptionInput(e: CustomEvent) {
    this.rule.details = e.detail.sourceEvent;
    this.descriptionInput.markAsValid();
    this.descriptionInput.showErrorIcon = false;

    this.validateRuleDescription();
  }

  private getTagsOptions() {
    const options: any = [];
    for (const tag of this.tags) {
      options.push({
        label: tag.name,
        value: tag.name,
        additionalData: {
          type: tag.type,
        },
        selected: this.rule.tags.find(item => item === tag.name) !== undefined,
      });
    }

    return options;
  }

  private handleStatusCheckboxChange(event) {
    this.rule.status = event.detail.checked ? Status.ENABLED : Status.DISABLED;
  }

  async handleValidation() {
    let valid: boolean = this.validateRuleName();
    valid = valid && this.validateRuleDescription();

    if (!valid) {
      return valid; // do not check rule name is something is invalid
    }

    try {
      const response = await ConsoleRequest.createRequest<any>({
        dataProviderType: DataProviderType.CORE,
        endpoint: this.gridType === GridType.DETECTION ? 'DetectionRules' : 'ExclusionRules',
        method: 'checkIfRuleNameIsUnique',
        payload: {
          ruleName: this.rule.name,
          companyId: this.rule.companyId ?? null,
          ruleId: this.rule._id ?? null,
        },
      });

      if (!response.success) {
        this.markFieldWithError(this.nameInput, translateFn('The rule name already exists'));
      }

      return response.success;
    } catch (e) {
      console.error(e);
      this.markFieldWithError(this.nameInput, translateFn('Error validating rule name'));
    }

    return false;
  }

  validateRuleName(): boolean {
    if (!this.rule.name) {
      this.markFieldWithError(this.nameInput, translateFn('Field is required'));
      return false;
    }

    if (this.rule.name.length > RULE_NAME_MAX_LENGTH) {
      this.markFieldWithError(
        this.nameInput,
        translateFn('The name can have a maximum of {nameMaxLength} characters.', {
          nameMaxLength: RULE_NAME_MAX_LENGTH,
        })
      );
      return false;
    }

    // This should match the regex from backend also
    const regexValid = new RegExp(/^[^\s<>\'\"][^<>\'\"]*$/, 'g').test(this.rule.name);

    if (!regexValid) {
      this.markFieldWithError(this.nameInput, translateFn('Rule name is invalid'));
      return false;
    }

    return true;
  }

  validateRuleDescription() {
    if (!this.rule.details || this.rule.details.length === 0) {
      return true;
    }

    if (this.rule.details && this.rule.details.length > RULE_DESCRIPTION_MAX_LENGTH) {
      this.markFieldWithError(this.descriptionInput, translateFn('Rule description is too long'));
      return false;
    }

    // This should match the regex from backend also
    const regexValid = new RegExp(/^[^\s<>\'\"][^<>\'\"]*$/, 'g').test(this.rule.details);

    if (!regexValid) {
      this.markFieldWithError(this.descriptionInput, translateFn('The rule description contains characters that are not allowed.'));
      return false;
    }

    GzTooltip.hide();
    return true;
  }

  markFieldWithError(field, message: string) {
    field.active = true;
    field.markAsInvalid();
    field.errorTooltipText = message;

    // GzTextAreaWebComponent
    if (field.showErrorIcon !== undefined) {
      field.showErrorIcon = true;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-rule-configuration': GzRuleConfigurationWebComponent;
  }
}
